import { useTranslation } from 'next-i18next'

import ArrowButton from '~/checklist/components/ArrowButton'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import {
  addActiveCompetency,
  removeActiveCompetency,
  selectActiveCompetencyList,
  selectScrollItemIdOnTopicPage,
} from '../slice/persistCurriculum.slice'
import { CurriculumMappingQuizTask } from '../types/mapping-quiz.types'

import PrerequisitesBootcampModule from './PrerequisitesBootcampModule'

interface PrerequisitesListProps {
  prerequisites: CurriculumMappingQuizTask[]
  userId: number | null
  viewKey: string
  disableScroll?: boolean
  grade?: string
  topic?: string
}

export default function PrerequisitesList({
  prerequisites = [],
  userId,
  viewKey,
  disableScroll = false,
  grade,
  topic,
}: PrerequisitesListProps): React.ReactElement {
  const dispatch = useAppDispatch()

  const activeCompetencyIds = useAppSelector(selectActiveCompetencyList)
  const scrollItemId = useAppSelector(selectScrollItemIdOnTopicPage)
  const collapseButtonId = viewKey.replace(/^\//, '').replace(/\//g, '#')
  const togglePrerequisiteTopicDropdown = (id: string, isExpanded: boolean): void => {
    if (isExpanded) {
      dispatch(removeActiveCompetency(id))
    } else {
      dispatch(addActiveCompetency(id))
    }
  }

  const handleCollapseClick = (): void => {
    dispatch(isCollapseButtonActive ? removeActiveCompetency(collapseButtonId) : addActiveCompetency(collapseButtonId))
  }

  const isCollapseButtonActive = activeCompetencyIds.includes(collapseButtonId)
  const itemsToDisplay = isCollapseButtonActive ? prerequisites : prerequisites.slice(0, 3)

  const { t } = useTranslation('curriculum')

  return (
    <div className="my-5 px-2">
      <div className="flex justify-center flex-wrap max-w-screen-xl mx-auto gap-8">
        {itemsToDisplay.map((item, index) => {
          const { subject, subjectList } = item
          const topicId = `${subject}-${subjectList[0].slug}-${subjectList[0].levels[0].levelIds[0]}`
          const isTopicExpanded = activeCompetencyIds.includes(topicId)
          return (
            <div className="relative w-80" key={topicId}>
              {item.isCompleted && (
                <img
                  className="absolute z-5 -top-8 -left-5"
                  height={60}
                  src="https://assets.houseofmath.com/icons/emojis/clapping-hands-animated.png"
                  width={60}
                />
              )}
              <div
                className={`rounded py-5 border border-white ${
                  item.isCompleted ? 'bg-purple-darker' : 'bg-white/30 backdrop-blur-md'
                }`}
              >
                <div
                  className={`${isTopicExpanded ? 'h-auto' : 'h-fit'} cursor-pointer`}
                  onClick={() => togglePrerequisiteTopicDropdown(topicId, isTopicExpanded)}
                >
                  <div className="relative flex items-start justify-between h-full w-full px-3">
                    <p className="text-6 w-full text-white text-center leading-7 tracking-wider font-bold break-words py-1">
                      {item.subject}
                    </p>

                    <button className="absolute top-0 right-3">
                      <ArrowButton color="text-white" expanded={isTopicExpanded} />
                    </button>
                  </div>
                </div>
                <div
                  className={`transition-all delay-150 duration-500 overflow-hidden ${
                    isTopicExpanded ? 'max-h-800 pb-1.5' : 'max-h-0'
                  }`}
                >
                  {item.subjectList.map((curriculumSubject, subjectIndex: number) => {
                    const moduleIndex =
                      subjectIndex +
                      1 +
                      prerequisites
                        .slice(0, index)
                        .reduce((acc, prerequisite) => acc + prerequisite.subjectList.length, 0)
                    const subjectId = curriculumSubject.levels[0].levelIds[0]
                    const isSubjectExpanded = activeCompetencyIds.includes(subjectId)

                    return (
                      <PrerequisitesBootcampModule
                        curriculumSubject={curriculumSubject}
                        disableScroll={disableScroll}
                        grade={grade}
                        isSubjectExpanded={isSubjectExpanded}
                        key={curriculumSubject.slug}
                        moduleIndex={moduleIndex}
                        onClick={togglePrerequisiteTopicDropdown}
                        scrollItemId={scrollItemId}
                        subjectId={subjectId}
                        subTopic={curriculumSubject.title}
                        topic={topic}
                        userId={userId}
                        viewKey={viewKey}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {prerequisites.length > 3 && (
        <div className="mt-6 text-center">
          <button
            className="text-white w-56 h-12 bg-purple rounded border font-bold tracking-wider text-xs"
            onClick={handleCollapseClick}
          >
            {isCollapseButtonActive ? t('mappingQuiz.showLess') : t('mappingQuiz.showMoreMustKnows')}
          </button>
        </div>
      )}
    </div>
  )
}
